import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fmtErr } from "../api";
import Seo from "../components/Seo";
import { COUNTRIES } from "../constants";
import { Truck, Building2 } from "lucide-react";

export default function AuthPage() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [role, setRole] = useState(params.get("role") === "courier" ? "courier" : "company");
  const [form, setForm] = useState({ email: "", password: "", name: "", company_name: "", phone: "", country: "", vehicle_type: "van_small" });
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const go = (u) => {
    if (u.status === "pending") { setRegistered(true); return; }
    navigate(u.role === "admin" ? "/control" : u.role === "company" ? "/company" : "/courier");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const u = mode === "login"
        ? await login(form.email, form.password, remember)
        : await register({ ...form, role, company_name: role === "company" ? form.company_name : null, vehicle_type: role === "courier" ? form.vehicle_type : null });
      if (u?.mfa_required) { setMfaToken(u.mfa_token); }
      else {
        if (mode === "register") { try { localStorage.setItem("cr_tour_pending", role); } catch { /* private mode */ } }
        go(u);
      }
    } catch (err) {
      setError(fmtErr(err));
    }
    setBusy(false);
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { default: apiClient } = await import("../api");
      const { data } = await apiClient.post("/auth/mfa/verify", { mfa_token: mfaToken, code: mfaCode });
      window.location.href = data.role === "admin" ? "/control" : data.role === "company" ? "/company" : "/courier";
    } catch (err) { setError(fmtErr(err)); }
    setBusy(false);
  };

  if (registered) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="pending-approval">
        <Seo title="Check your inbox | CourierRadar" noindex />
        <h1 className="text-2xl font-bold tracking-tight">Check your inbox</h1>
        <p className="text-sm text-zinc-600 mt-3">We sent a verification link to <strong>{form.email}</strong>. Verify your email, then our team reviews your account — you'll get an email once approved. After approval, choose a plan to start working.</p>
        <Link to="/" className="inline-block mt-6 text-sm font-semibold text-black underline underline-offset-2">Back to homepage</Link>
      </div>
    );
  }

  if (mfaToken) {
    return (
      <div className="max-w-md mx-auto px-4 py-20" data-testid="mfa-challenge">
        <Seo title="Two-factor authentication | CourierRadar" noindex />
        <h1 className="text-2xl font-bold tracking-tight mb-2">Two-factor authentication</h1>
        <p className="text-sm text-zinc-500 mb-5">Enter the 6-digit code from your authenticator app.</p>
        <form onSubmit={submitMfa} className="space-y-3">
          <input data-testid="mfa-code" inputMode="numeric" maxLength={6} required placeholder="000000"
            value={mfaCode} onChange={(e) => setMfaCode(e.target.value)}
            className="w-full border border-zinc-200 focus:border-black rounded-lg text-center text-lg tracking-[0.4em] font-mono bg-white px-3.5 py-3 outline-none" />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <button data-testid="mfa-submit" disabled={busy}
            className="w-full bg-black text-white hover:bg-zinc-800 font-medium transition-colors px-5 py-3 rounded-lg text-sm disabled:opacity-40">Verify</button>
        </form>
      </div>
    );
  }

  const inputCls = "cr-input";

  return (
    <div className="max-w-md mx-auto px-4 py-14" data-testid="auth-page">
      <Seo title={`${mode === "login" ? "Sign in" : "Create account"} | CourierRadar`} noindex />
      <h1 className="text-3xl font-extrabold tracking-tight text-black">{mode === "login" ? "Welcome back" : "Join CourierRadar"}</h1>
      <p className="text-sm text-zinc-500 mt-2">{mode === "login" ? "Sign in to your account." : "Every account is verified by our team."}</p>

      <div className="flex gap-2 mt-6 mb-6 bg-zinc-100 p-1 rounded-lg">
        {["login", "register"].map((m) => (
          <button key={m} data-testid={`mode-${m}`} onClick={() => setMode(m)}
            className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${mode === m ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"}`}>
            {m === "login" ? "Sign in" : "Register"}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-3.5">
        {mode === "register" && (
          <div className="grid grid-cols-2 gap-3" data-testid="role-select">
            {[{ id: "courier", label: "Courier", icon: Truck, sub: "Deliver & earn" },
              { id: "company", label: "Company", icon: Building2, sub: "Ship & dispatch" }].map((r) => (
              <button type="button" key={r.id} data-testid={`role-${r.id}`} onClick={() => setRole(r.id)}
                className={`border rounded-xl p-4 text-left transition-all ${role === r.id ? "border-[#0A0A0A] ring-2 ring-[#0A0A0A]/20 bg-zinc-50" : "border-zinc-200 hover:border-zinc-300"}`}>
                <r.icon className={`w-5 h-5 mb-2 ${role === r.id ? "text-[#0A0A0A]" : "text-zinc-400"}`} />
                <p className="text-sm font-bold">{r.label}</p>
                <p className="text-[11px] text-zinc-500">{r.sub}</p>
              </button>
            ))}
          </div>
        )}
        {mode === "register" && (
          <>
            <input data-testid="register-name" required placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} />
            {role === "company" && <input data-testid="register-company" required placeholder="Company name" value={form.company_name} onChange={set("company_name")} className={inputCls} />}
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="register-country" required value={form.country} onChange={set("country")} className={inputCls}>
                <option value="">Country</option>
                {COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
              </select>
              <input data-testid="register-phone" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className={inputCls} />
            </div>
            {role === "courier" && (
              <select data-testid="register-vehicle" value={form.vehicle_type} onChange={set("vehicle_type")} className={inputCls}>
                <option value="bike">Cargo bike</option><option value="car">Car</option>
                <option value="van_small">Small van</option><option value="van_large">Large van</option>
                <option value="box_truck">Box truck</option><option value="semi_trailer">Semi-trailer</option>
              </select>
            )}
          </>
        )}
        <input data-testid="auth-email" type="email" required placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} />
        <input data-testid="auth-password" type="password" required minLength={8} placeholder="Password (min 8 chars)" value={form.password} onChange={set("password")} className={inputCls} />
        {mode === "login" && (
          <label className="flex items-center gap-2 text-xs text-[#78756E] cursor-pointer select-none">
            <input type="checkbox" data-testid="remember-me" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-[#1C1B1A]" />
            Stay signed in on this device
          </label>
        )}
        {error && <p data-testid="auth-error" className="text-xs text-red-600">{error}</p>}
        <button data-testid="auth-submit-btn" disabled={busy}
          className="w-full bg-black text-white hover:bg-zinc-800 active:scale-[0.98] font-medium transition-all px-5 py-3 rounded-lg shadow-sm text-sm disabled:opacity-50">
          {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
        </button>
        {mode === "login" && (
          <button type="button" data-testid="forgot-password-btn" onClick={async () => {
            if (!form.email) return setError("Enter your email first");
            const { default: apiClient } = await import("../api");
            await apiClient.post("/auth/forgot-password", { email: form.email }).catch(() => {});
            setError("");
            setForgotOpen(true);
          }} className="w-full text-center text-xs text-zinc-400 hover:text-black transition-colors">Forgot password?</button>
        )}
        {forgotOpen && <p data-testid="forgot-sent" className="text-xs text-zinc-500 text-center">If the account exists, a reset link is on its way to {form.email}.</p>}
      </form>
      <p className="text-[11px] text-zinc-400 mt-4 text-center">By continuing you accept our <Link to="/p/terms" className="underline">Terms</Link> and <Link to="/p/privacy" className="underline">Privacy Policy</Link>.</p>
    </div>
  );
}
